import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import RecordShell from "../../components/RecordShell";
import { fmtDate, eur, PAYMENT_STATUS, INVOICE_STATUS } from "../../lib/helpers";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import AuthImg from "../../components/AuthImg";
import AuthFileLink from "../../components/AuthFileLink";
import Card from "../../components/Card";
import SectionHeader from "../../components/SectionHeader";

const CURRENCIES = ["EUR", "AED", "USD", "GBP", "CHF", "MAD"];

export default function ClientSettings() {
  const [a, setA] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [billing, setBilling] = useState(null);
  const [bf, setBf] = useState({ currency: "", billing_address: "", tax_number: "", siret: "" });
  const [savingBf, setSavingBf] = useState(false);
  const [editBf, setEditBf] = useState(false);
  const [billPage, setBillPage] = useState(1);
  const [editCo, setEditCo] = useState(false);
  const [cf, setCf] = useState({ address: "", postal_code: "", city: "", country: "", company_phone: "" });
  const [savingCo, setSavingCo] = useState(false);

  const loadAccount = () => api.get("/client/account").then((r) => setA(r.data));
  const loadBilling = () => api.get("/client/billing").then((r) => { setBilling(r.data); setBf({ currency: r.data.billing_info?.currency || "", billing_address: r.data.billing_info?.billing_address || "", tax_number: r.data.billing_info?.tax_number || "", siret: r.data.siret || "" }); });
  useEffect(() => { loadAccount(); api.get("/client/contacts").then((r) => setContacts(r.data)); loadBilling(); }, []);
  if (!a) return null;

  const startEditBf = () => { setBf({ currency: billing?.billing_info?.currency || "", billing_address: billing?.billing_info?.billing_address || "", tax_number: billing?.billing_info?.tax_number || "", siret: billing?.siret || "" }); setEditBf(true); };
  const saveBilling = async () => {
    setSavingBf(true);
    try { await api.put("/client/billing-info", bf); toast.success("Informations enregistrées — Metara a été notifiée"); setEditBf(false); loadBilling(); }
    catch { toast.error("Échec de l'enregistrement"); }
    finally { setSavingBf(false); }
  };

  const startEditCo = () => { setCf({ address: a.address || "", postal_code: a.postal_code || "", city: a.city || "", country: a.country || "", company_phone: a.company_phone || "" }); setEditCo(true); };
  const saveCompany = async () => {
    setSavingCo(true);
    try { await api.put("/client/company-info", cf); toast.success("Informations enregistrées — Metara a été notifiée"); setEditCo(false); loadAccount(); }
    catch { toast.error("Échec de l'enregistrement"); }
    finally { setSavingCo(false); }
  };

  // ---------- MON ENTREPRISE ----------
  const renderEntreprise = () => {
    return (
      <div className="space-y-6" data-testid="settings-company">
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl text-marine">Informations générales</h2>
            {!editCo && <button onClick={startEditCo} data-testid="company-edit-btn" className="text-sm border border-marine/25 text-marine rounded-sm px-3 py-1.5 hover:bg-marine hover:text-white transition-colors flex items-center gap-1.5"><Icons.Pencil size={16} /> Modifier</button>}
          </div>
          {!editCo ? (
            <>
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4 text-sm">
                <div><div className="text-xs uppercase tracking-wider text-marine/80 mb-1">Entreprise</div><div className="text-marine font-medium">{a.name}</div></div>
                <div><div className="text-xs uppercase tracking-wider text-marine/80 mb-1">Entreprise Metara depuis</div><div className="text-marine">{a.client_since ? fmtDate(a.client_since) : "—"}</div></div>
                <div><div className="text-xs uppercase tracking-wider text-marine/80 mb-1">E-mail</div><div className="text-marine flex items-center gap-1.5"><Icons.Mail size={16} className="text-marine" /> {a.email}</div></div>
                <div><div className="text-xs uppercase tracking-wider text-marine/80 mb-1">Téléphone (gestion de projet)</div><div className="text-marine flex items-center gap-1.5"><Icons.Phone size={16} className="text-marine" /> {a.company_phone || "—"}</div></div>
                <div className="sm:col-span-2"><div className="text-xs uppercase tracking-wider text-marine/80 mb-1">Adresse</div><div className="text-marine">{a.address || "—"}</div></div>
                <div><div className="text-xs uppercase tracking-wider text-marine/80 mb-1">Code postal</div><div className="text-marine">{a.postal_code || "—"}</div></div>
                <div><div className="text-xs uppercase tracking-wider text-marine/80 mb-1">Ville</div><div className="text-marine">{a.city || "—"}</div></div>
                <div><div className="text-xs uppercase tracking-wider text-marine/80 mb-1">Pays</div><div className="text-marine">{a.country || "—"}</div></div>
              </div>
              <p className="text-xs text-marine/80 mt-4 flex items-center gap-1.5"><Icons.Info size={16} className="text-marine" /> Vous pouvez modifier l'adresse et le téléphone de gestion de projet de votre entreprise ci-dessus ; toute modification notifie Metara. Ce téléphone sert aux échanges opérationnels de Metara avec votre entreprise — il ne remplace pas votre contact personnel, géré dans « Mon Compte ».</p>
            </>
          ) : (
            <div data-testid="company-edit-form" className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <label className="text-sm sm:col-span-2">Adresse
                  <input value={cf.address} onChange={(e) => setCf({ ...cf, address: e.target.value })} data-testid="company-address" className="inp mt-1" placeholder="Adresse de l'entreprise" />
                </label>
                <label className="text-sm">Téléphone (gestion de projet)
                  <input value={cf.company_phone} onChange={(e) => setCf({ ...cf, company_phone: e.target.value })} data-testid="company-phone" className="inp mt-1" placeholder="Téléphone pour la gestion de projet" />
                </label>
                <label className="text-sm">Code postal
                  <input value={cf.postal_code} onChange={(e) => setCf({ ...cf, postal_code: e.target.value })} data-testid="company-postal" className="inp mt-1" />
                </label>
                <label className="text-sm">Ville
                  <input value={cf.city} onChange={(e) => setCf({ ...cf, city: e.target.value })} data-testid="company-city" className="inp mt-1" />
                </label>
                <label className="text-sm">Pays
                  <input value={cf.country} onChange={(e) => setCf({ ...cf, country: e.target.value })} data-testid="company-country" className="inp mt-1" />
                </label>
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setEditCo(false)} className="px-4 py-2 text-sm text-marine/80">Annuler</button>
                <button onClick={saveCompany} disabled={savingCo} data-testid="company-save" className="bg-marine text-white rounded-sm px-5 py-2 text-sm hover:bg-marine-hover transition-colors disabled:opacity-50">{savingCo ? "Enregistrement…" : "Enregistrer"}</button>
              </div>
            </div>
          )}
        </Card>

        <Card>
          <SectionHeader title="Prestations actives" />
          <div className="flex flex-wrap gap-2" data-testid="settings-services">
            {(a.services || []).length === 0 && <p className="text-sm text-marine/80">Aucune prestation active.</p>}
            {(a.services || []).map((s) => <span key={s} className="text-xs px-3 py-1 rounded-sm bg-sable border border-marine/30 text-marine">{s}</span>)}
          </div>
        </Card>

        {(a.managed_networks || []).length > 0 && (
          <Card>
            <SectionHeader title="Réseaux sociaux gérés" />
            <div className="space-y-2">{a.managed_networks.map((n, i) => (<div key={i} className="flex items-center justify-between text-sm border-b border-marine/5 pb-2 last:border-0"><span className="text-marine">{n.network}</span><span className="text-marine/80">{n.handle}</span></div>))}</div>
          </Card>
        )}

        <Card>
          <SectionHeader title="Documents contractuels" />
          <div className="space-y-2" data-testid="settings-contracts">
            {(a.contract_docs || []).length === 0 && <p className="text-sm text-marine/80">Aucun document.</p>}
            {(a.contract_docs || []).map((d, i) => (<div key={i} className="flex items-center justify-between text-sm"><span className="text-marine">{d.name}</span><AuthFileLink path={d.path} className="text-marine flex items-center gap-1 hover:underline"><Icons.Download size={16} /> Télécharger</AuthFileLink></div>))}
          </div>
        </Card>
      </div>
    );
  };

  // ---------- MES CONTACTS ----------
  const renderContacts = () => (
    <div className="space-y-4" data-testid="settings-contacts">
      {contacts.length === 0 && <Card><p className="text-sm text-marine/80">Aucun contact associé à votre entreprise.</p></Card>}
      <div className="grid sm:grid-cols-2 gap-4">
        {contacts.map((c) => (
          <div key={c.id} data-testid={`settings-contact-${c.id}`} className="bg-sable rounded-sm border border-marine/10 shadow-sm p-5">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-full bg-marine-50 text-marine flex items-center justify-center overflow-hidden shrink-0 font-display">
                {c.avatar_url ? <AuthImg path={c.avatar_url} alt={c.name} className="w-full h-full object-cover" /> : (c.name || "?").charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-marine">{c.name}</div>
                {c.function && <div className="text-xs text-marine/80">{c.function}</div>}
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {c.is_referent && <span className="text-[9px] uppercase tracking-wider bg-marine-50 text-marine border border-marine/30 px-1.5 py-0.5 rounded-sm inline-flex items-center gap-1"><Icons.Star size={16} /> Référent</span>}
                  {c.is_billing && <span className="text-[9px] uppercase tracking-wider bg-bleu-50 text-bleu border border-bleu/30 px-1.5 py-0.5 rounded-sm inline-flex items-center gap-1"><Icons.Calculator size={16} /> Comptabilité</span>}
                </div>
                <div className="text-xs text-marine/80 mt-2 space-y-0.5">
                  {c.email && <div className="flex items-center gap-1.5"><Icons.Mail size={16} className="text-marine" /> {c.email}</div>}
                  {(c.phone || c.mobile) && <div className="flex items-center gap-1.5"><Icons.Phone size={16} className="text-marine" /> {c.phone || c.mobile}</div>}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // ---------- FACTURATION ----------
  const renderFacturation = () => {
    const bc = billing?.billing_contact;
    const invoices = (billing?.history || []).filter((it) => it.invoice_url || it.invoice_link);
    const PAGE = 10;
    const totalPages = Math.max(1, Math.ceil(invoices.length / PAGE));
    const page = Math.min(billPage, totalPages);
    const shown = invoices.slice((page - 1) * PAGE, page * PAGE);
    return (
      <div className="space-y-6" data-testid="settings-billing">
        {bc && (
          <Card>
            <SectionHeader title="Interlocuteur comptable" />
            <div className="flex items-center gap-3" data-testid="settings-billing-contact">
              <div className="w-11 h-11 rounded-full bg-bleu-50 text-bleu flex items-center justify-center overflow-hidden shrink-0 font-display">
                {bc.avatar_url ? <AuthImg path={bc.avatar_url} alt={bc.name} className="w-full h-full object-cover" /> : (bc.name || "?").charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <div className="text-sm font-medium text-marine">{bc.name}</div>
                {bc.function && <div className="text-xs text-marine/80">{bc.function}</div>}
                <div className="text-xs text-marine/80 mt-1 space-y-0.5">
                  {bc.email && <div className="flex items-center gap-1.5"><Icons.Mail size={16} className="text-marine" /> {bc.email}</div>}
                  {(bc.phone || bc.mobile) && <div className="flex items-center gap-1.5"><Icons.Phone size={16} className="text-marine" /> {bc.phone || bc.mobile}</div>}
                </div>
              </div>
            </div>
          </Card>
        )}

        <Card>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl text-marine">Informations de facturation</h2>
            {!editBf && <button onClick={startEditBf} data-testid="billing-edit-btn" className="text-sm border border-marine/25 text-marine rounded-sm px-3 py-1.5 hover:bg-marine hover:text-white transition-colors flex items-center gap-1.5"><Icons.Pencil size={16} /> Modifier</button>}
          </div>
          {!editBf ? (
            <>
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4 text-sm">
                <div><div className="text-xs uppercase tracking-wider text-marine/80 mb-1">Devise de facturation</div><div className="text-marine">{billing?.billing_info?.currency || "—"}</div></div>
                <div><div className="text-xs uppercase tracking-wider text-marine/80 mb-1">Numéro fiscal (TRN / TVA)</div><div className="text-marine">{billing?.billing_info?.tax_number || "—"}</div></div>
                <div><div className="text-xs uppercase tracking-wider text-marine/80 mb-1">Numéro SIRET</div><div className="text-marine">{billing?.siret || "—"}</div></div>
                <div className="sm:col-span-2"><div className="text-xs uppercase tracking-wider text-marine/80 mb-1">Adresse de facturation</div><div className="text-marine">{billing?.billing_info?.billing_address || "—"}</div></div>
              </div>
              <p className="text-xs text-marine/80 mt-4 flex items-center gap-1.5"><Icons.Info size={16} className="text-marine" /> Ces informations peuvent être complétées par vous ou par Metara ; toute modification notifie l'agence.</p>
            </>
          ) : (
            <div data-testid="billing-edit-form" className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <label className="text-sm">Devise de facturation
                  <select value={bf.currency} onChange={(e) => setBf({ ...bf, currency: e.target.value })} data-testid="billing-currency" className="inp mt-1">
                    <option value="">— Sélectionner —</option>
                    {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </label>
                <label className="text-sm">Numéro fiscal (TRN / TVA)
                  <input value={bf.tax_number} onChange={(e) => setBf({ ...bf, tax_number: e.target.value })} data-testid="billing-tax" className="inp mt-1" placeholder="Ex. 100xxxxxxxxxxxx3" />
                </label>
                <label className="text-sm">Numéro SIRET
                  <input value={bf.siret} onChange={(e) => setBf({ ...bf, siret: e.target.value })} data-testid="billing-siret" className="inp mt-1" placeholder="000 000 000 00000" />
                </label>
                <label className="text-sm sm:col-span-2">Adresse de facturation
                  <textarea rows={2} value={bf.billing_address} onChange={(e) => setBf({ ...bf, billing_address: e.target.value })} data-testid="billing-address" className="inp mt-1" placeholder="Adresse figurant sur les factures" />
                </label>
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setEditBf(false)} className="px-4 py-2 text-sm text-marine/80">Annuler</button>
                <button onClick={saveBilling} disabled={savingBf} data-testid="billing-info-save" className="bg-marine text-white rounded-sm px-5 py-2 text-sm hover:bg-marine-hover transition-colors disabled:opacity-50">{savingBf ? "Enregistrement…" : "Enregistrer"}</button>
              </div>
            </div>
          )}
        </Card>

        <Card>
          <SectionHeader title="Mes factures" />
          <p className="text-xs text-marine/80 mb-3">Retrouvez et téléchargez ici l'ensemble de vos factures.</p>
          {invoices.length === 0 ? <p className="text-sm text-marine/80">Aucune facture disponible pour le moment.</p> : (
            <>
            <div className="rounded-sm border border-marine/10 overflow-hidden" data-testid="billing-history">
              <div className="grid grid-cols-[1fr_82px_0px_140px] sm:grid-cols-[1fr_82px_80px_140px] md:grid-cols-[1fr_110px_80px_150px] gap-3 px-4 py-2.5 bg-sable border-b border-marine/10 text-xs uppercase tracking-wider text-marine/80">
                <span className="min-w-0 truncate">Facture</span><span className="text-right">Montant</span><span className="text-right hidden sm:block">Date</span><span className="text-right">Statut</span>
              </div>
              <div className="divide-y divide-marine/5">
                {shown.map((it) => {
                  const s = PAYMENT_STATUS[it.status] || PAYMENT_STATUS.a_venir;
                  const hasFile = !!it.invoice_url;
                  const externalHref = !hasFile ? (it.invoice_link || null) : null;
                  const LinkTag = hasFile ? AuthFileLink : "a";
                  const linkProps = hasFile ? { path: it.invoice_url } : { href: externalHref, target: "_blank", rel: "noreferrer" };
                  return (
                    <div key={`${it.project_id}-${it.id}`} data-testid={`billing-row-${it.id}`} className="px-4 py-3 bg-creme">
                      <div className="grid grid-cols-[1fr_82px_0px_140px] sm:grid-cols-[1fr_82px_80px_140px] md:grid-cols-[1fr_110px_80px_150px] gap-3 items-center">
                        <div className="min-w-0">
                          <LinkTag {...linkProps} data-testid={`billing-invoice-${it.id}`} className="text-sm font-medium text-marine hover:underline inline-flex items-center gap-1.5 truncate"><Icons.FileText size={16} className="shrink-0" /> {it.invoice_number || "Voir la facture"}</LinkTag>
                          <div className="text-xs text-marine/80 truncate">{it.project}{it.label ? ` · ${it.label}` : ""}</div>
                        </div>
                        <div className="text-sm font-display text-marine text-right">{eur(it.amount, it.currency)}</div>
                        <div className="text-xs text-marine/80 text-right hidden sm:block">{it.due_date ? fmtDate(it.due_date) : "—"}</div>
                        <div className="text-right"><span className={`text-[9px] px-2 py-0.5 rounded-sm border ${s.cls}`}>{s.label}</span></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-3" data-testid="billing-pagination">
                <button onClick={() => setBillPage(page - 1)} disabled={page <= 1} data-testid="billing-prev" className="flex items-center gap-1 text-sm text-marine disabled:opacity-40 disabled:cursor-not-allowed hover:underline"><Icons.ChevronLeft size={16} /> Précédent</button>
                <span className="text-xs text-marine/80">Page {page} / {totalPages} · {invoices.length} facture(s)</span>
                <button onClick={() => setBillPage(page + 1)} disabled={page >= totalPages} data-testid="billing-next" className="flex items-center gap-1 text-sm text-marine disabled:opacity-40 disabled:cursor-not-allowed hover:underline">Suivant <Icons.ChevronRight size={16} /></button>
              </div>
            )}
            </>
          )}
        </Card>
      </div>
    );
  };

  const header = (
    <div className="bg-sable rounded-sm border border-marine/10 shadow-sm p-5">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-bordeaux mb-0.5">Votre espace</div>
        <h1 className="font-display text-2xl text-marine">Paramètres</h1>
      </div>
    </div>
  );

  const sections = [
    { id: "entreprise", label: "Mon entreprise", icon: "Building2", render: renderEntreprise },
    { id: "contacts", label: "Mes contacts", icon: "Contact2", render: renderContacts },
    { id: "facturation", label: "Facturation", icon: "Wallet", render: renderFacturation },
  ];

  return (
    <div>
      <RecordShell testid="client-settings-record" breadcrumb={[{ label: "Paramètres" }]} header={header} sections={sections} />
    </div>
  );
}
